import { Box, Table, Text } from '@/components';
import type { UpdatedProjectSummaryModel } from '@/features/project/projects/_models/project';
import { useLocaledAmount } from '@/providers';
import { formatValueByUnit } from '@/utils';
import Big from 'big.js';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type Props = {
  summary: z.infer<typeof UpdatedProjectSummaryModel>;
};

type TableDataSource = {
  label: string;
  key: string;
  newRequested: number | null;
  newStarted: number | null;
  continuation: number | null;
};

export const ProjectStatusSummary: FC<Props> = ({ summary }) => {
  const { t, i18n } = useTranslation();
  const { unit } = useLocaledAmount();

  const statusSummaryDataSource = useMemo<TableDataSource[]>(
    () => [
      {
        label: t('count'),
        key: 'count',
        newRequested: summary.newlyRequestedProjectCount,
        newStarted: summary.newlyStartedProjectCount,
        continuation: summary.continuingProjectCount,
      },
      {
        label: t('previous-fiscal-year-executed'),
        key: 'previousYearExecuted',
        newRequested:
          summary.previousYearExecutedAmountUnderNewlyRequestedProject,
        newStarted: summary.previousYearExecutedAmountUnderNewlyStartedProject,
        continuation: summary.previousYearExecutedAmountUnderContinuingProject,
      },
      {
        label: t('initial-finalized-amount'),
        key: 'currentYearAmount',
        newRequested:
          summary.finalizedAmountForInitialBudgetUnderNewlyRequestedProject,
        newStarted:
          summary.finalizedAmountForInitialBudgetUnderNewlyStartedProject,
        continuation:
          summary.finalizedAmountForInitialBudgetUnderContinuingProject,
      },
      {
        label: t('next-request-amount'),
        key: 'nextRequestAmount',
        newRequested: summary.requestedAmountUnderNewlyRequestedProject,
        newStarted: summary.requestedAmountUnderNewlyStartedProject,
        continuation: summary.requestedAmountUnderContinuingProject,
      },
    ],
    [summary, t],
  );

  const getRenderedValue = (value: number | null, record: TableDataSource) => {
    const displayValue =
      value === null
        ? null
        : record.key === 'count'
          ? value.toString()
          : formatValueByUnit(value, {
              operation:
                record.key === 'previousYearExecuted' ? 'floor' : 'round',
              unit,
            });

    return (
      <>
        <Text variant="h5" weight="bold" text={displayValue} />
        <Text
          variant="footnote"
          color="secondary"
          text={
            record.key === 'count'
              ? t('project-count-unit')
              : t('thousand-yen-unit')
          }
        />
      </>
    );
  };

  return (
    <Box marginBottom={16}>
      <Table
        key={i18n.language}
        dataSource={statusSummaryDataSource}
        columns={[
          {
            title: '',
            key: 'label',
            dataIndex: 'label',
            render: (value) => (
              <Text variant="footnote" color="secondary" text={value} />
            ),
          },
          {
            title: t('project-category-continuation'),
            key: 'continuation',
            dataIndex: 'continuation',
            align: 'right',
            render: getRenderedValue,
          },
          {
            title: t('project-category-new'),
            key: 'newStarted',
            dataIndex: 'newStarted',
            align: 'right',
            render: getRenderedValue,
          },
          {
            title: t('project-category-requesting'),
            key: 'newRequested',
            dataIndex: 'newRequested',
            align: 'right',
            render: getRenderedValue,
          },
          {
            title: t('total'),
            key: 'total',
            dataIndex: 'total',
            align: 'right',
            render: (_, record) => {
              const targets = [
                record.newRequested,
                record.newStarted,
                record.continuation,
              ];
              const isAllNull = targets.every((v) => v === null);
              const totalAmount = isAllNull
                ? null
                : new Big(
                    targets.reduce((acc, cur) => acc.add(cur ?? 0), new Big(0)),
                  ).toNumber();

              const displayValue = isAllNull
                ? null
                : record.key === 'count'
                  ? totalAmount?.toString()
                  : formatValueByUnit(totalAmount, {
                      operation: 'round',
                      unit,
                    });

              return (
                <>
                  <Text variant="h5" weight="bold" text={displayValue} />
                  <Text
                    variant="footnote"
                    color="secondary"
                    text={
                      record.key === 'count'
                        ? t('project-count-unit')
                        : t('thousand-yen-unit')
                    }
                  />
                </>
              );
            },
          },
        ]}
        pagination={false}
      />
    </Box>
  );
};
